import { useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router';
import { z } from 'zod';

import {
  Button,
  FormField,
  Input,
  useDialogContext,
} from '@components/primitives';

import { useCreateTeam } from '@queries/team';
import { USER_QUERY_KEY, userTeamsSelector, useUserQuery } from '@queries/user';

const buildTeamCreateSchema = (teamNames) =>
  z.object({
    name: z
      .string()
      .trim()
      .min(2, { message: 'Team name must be at least 2 characters long' })
      .max(30, { message: "Team name can't be longer than 30 characters" })
      .regex(/^[\w- .',]+(\b|[-.'])$/, {
        message:
          "Team name can only contain alphanumeric characters, spaces, and ',.-",
      })
      .refine((value) => !teamNames.includes(value?.toUpperCase()), {
        message: 'Looks like you already have a team with that name',
      }),
  });

export const TeamCreate = () => {
  const navigate = useNavigate();
  const client = useQueryClient();
  const { mutateAsync } = useCreateTeam();
  const dialog = useDialogContext();
  const userTeamsSub = useUserQuery({ select: userTeamsSelector });
  const teamNames = useMemo(
    () => (userTeamsSub.data ?? []).map((team) => team.name.toUpperCase()),
    [userTeamsSub.data]
  );

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors, isValid },
  } = useForm({
    defaultValues: { name: '' },
    resolver: zodResolver(buildTeamCreateSchema(teamNames)),
  });

  const submissionHandler = async ({ name }) => {
    try {
      const { team } = await mutateAsync({ name });
      await client.invalidateQueries({ queryKey: USER_QUERY_KEY });
      await client.fetchQuery({ queryKey: USER_QUERY_KEY });
      dialog.close();
      navigate(`/${team.uuid}`);
    } catch {
      toast.error('Failed to create team');
    }
  };

  return (
    <form onSubmit={handleSubmit(submissionHandler)}>
      <FormField
        className="mb-4"
        label="Team Name"
        error={errors.name?.message}
      >
        <Input
          placeholder="e.g. PlanetExpress"
          disabled={isSubmitting}
          autoComplete="off"
          {...register('name')}
        />
      </FormField>

      <Button variant="primary" disabled={!isValid} loading={isSubmitting}>
        Continue
      </Button>
    </form>
  );
};
